/**
 * Plans to staff — every plan still being staffed, one block per plan, across ALL plans
 * (Staffing Requests page). A plan leaves the list once its Resourcing status is Staffed;
 * Lost / Superseded / Closed plans and plans on a Closed Solution never show.
 *
 * Every plan starts open (2026-10-19): this page is where the RM works through them, so
 * hiding the grids behind a click only added one. A plan's header carries its name (a link
 * to the plan), where it sits, how many roles are still open and the live Sales and
 * Resourcing status controls; under it, the plan page's own working grid in its staffing
 * view — who is on each line and their hours, no money, nothing to edit but the staffing.
 * Setting Resourcing to Staffed drops the plan off the list.
 *
 * Where the viewer may request on the plan's Solution, a Swap can be raised from the plan.
 */

import { Link } from "@tanstack/react-router";
import { ArrowRightLeft, ChevronDown, ChevronRight, UserSearch } from "lucide-react";
import { useState } from "react";
import { PlanResourcingControl } from "@/components/plans/PlanResourcingControl";
import {
  PlanStatusMenu,
  STATUS_TRIGGER_CLASS,
  StatusDot,
} from "@/components/plans/PlanStatusControl";
import { planStatusDotClass, resourcingStatusDotClass } from "@/components/plans/PlansTable";
import { PlanWorkingGrid } from "@/components/plans/PlanWorkingGrid";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useResourcePlan } from "@/hooks/use-resource-plans";
import { useOpenRolesByPlan } from "@/hooks/use-staffing-requests";
import { planStatusLabel, resourcingStatusLabel } from "@/lib/plan-status-labels";
import { cn } from "@/lib/utils";
import type { OpenRolePlanGroup } from "@/schemas/staffing-requests";
import type { SwapPrefill } from "./NewStaffingRequestDialog";

interface Props {
  onSwap: (prefill: SwapPrefill) => void;
}

export function PlansToStaff({ onSwap }: Props) {
  const { data, isLoading, isError, error, refetch } = useOpenRolesByPlan();
  // Plans start open; this holds the ones the reader has folded away.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const openRoles = data?.reduce((n, g) => n + g.roles.length, 0) ?? 0;

  function toggle(planId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  }

  return (
    <section aria-labelledby="plans-to-staff-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="plans-to-staff-heading" className="text-lg font-semibold">
          Plans to staff
          {data && data.length > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {data.length} plan{data.length === 1 ? "" : "s"} · {openRoles} open role
              {openRoles === 1 ? "" : "s"}
            </span>
          )}
        </h2>
        <p className="text-sm text-muted-foreground">
          Plans not yet Staffed. Set one to Staffed and it leaves this list.
        </p>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-md" />
          ))}
        </div>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load the plans to staff."}
          onRetry={() => refetch()}
        />
      )}

      {data &&
        (data.length === 0 ? (
          <EmptyState
            icon={UserSearch}
            title="Nothing to staff"
            description="Every active plan is Staffed."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {data.map((group) => (
              <PlanBlock
                key={group.plan.id}
                group={group}
                open={!collapsed.has(group.plan.id)}
                onToggle={() => toggle(group.plan.id)}
                onSwap={onSwap}
              />
            ))}
          </div>
        ))}
    </section>
  );
}

function PlanBlock({
  group,
  open,
  onToggle,
  onSwap,
}: {
  group: OpenRolePlanGroup;
  open: boolean;
  onToggle: () => void;
  onSwap: (prefill: SwapPrefill) => void;
}) {
  // A folded plan does not load: the full plan is the heavy part of this page.
  const detail = useResourcePlan(open ? group.plan.id : null);
  const plan = open ? detail.data : undefined;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="rounded-md border border-border bg-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={open ? `Fold ${group.plan.name}` : `Open ${group.plan.name}`}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Chevron className="h-4 w-4" />
          </button>
          <span className="min-w-0">
            <Link
              to="/dashboard/plans/$planId"
              params={{ planId: group.plan.id }}
              className="block truncate text-sm font-semibold hover:underline"
              title="Open the plan"
            >
              {group.plan.name}
            </Link>
            <span className="block truncate text-xs text-muted-foreground">
              {group.solution.name}
              {group.account && ` · ${group.account.name}`}
            </span>
          </span>
        </div>

        <OpenRoleCount group={group} />

        <div className="flex flex-wrap items-center gap-2">
          {plan ? (
            <>
              <PlanStatusMenu plan={plan} />
              <PlanResourcingControl plan={plan} />
            </>
          ) : (
            <>
              <StaticStatus
                label="Sales"
                value={planStatusLabel(group.plan.status)}
                dot={planStatusDotClass(group.plan.status)}
              />
              <StaticStatus
                label="Resourcing"
                value={resourcingStatusLabel(group.plan.resourcingStatus)}
                dot={resourcingStatusDotClass(group.plan.resourcingStatus)}
              />
            </>
          )}
          {group.canRequest && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground"
              aria-label="Request a swap"
              title="Request a swap on this plan's Solution"
              onClick={() => onSwap({ solutionId: group.solution.id, seat: null })}
            >
              <ArrowRightLeft className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-border p-3">
          {detail.isLoading && <Skeleton className="h-48 w-full rounded-md" />}
          {detail.isError && (
            <ErrorState
              message={
                detail.error instanceof Error ? detail.error.message : "Could not load this plan."
              }
              onRetry={() => detail.refetch()}
            />
          )}
          {plan && <PlanWorkingGrid plan={plan} staffingOnly />}
        </div>
      )}
    </div>
  );
}

/** Open roles left on the plan, or that every line has someone on it. */
function OpenRoleCount({ group }: { group: OpenRolePlanGroup }) {
  const open = group.roles.length;
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
        open > 0 ? "bg-warning-100 text-warning-700" : "bg-success-100 text-success-700",
      )}
      title={`${group.lineCount} line${group.lineCount === 1 ? "" : "s"} on this plan`}
    >
      {open > 0 ? `${open} of ${group.lineCount} open` : "Every line filled"}
    </span>
  );
}

/** A status as the live control shows it, while the plan loads or is folded. */
function StaticStatus({ label, value, dot }: { label: string; value: string; dot: string }) {
  return (
    <span className={cn(STATUS_TRIGGER_CLASS, "cursor-default hover:bg-transparent")}>
      <span className="text-muted-foreground">{label}:</span>
      <StatusDot className={dot} />
      <span className="font-medium">{value}</span>
    </span>
  );
}
